import React, { ChangeEvent, useState } from 'react';
import { Col, Input, message, Modal, Radio, Row } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getBatchApproveSelections } from '@/api/contentReview';

interface BatchApproveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, remark: string) => Promise<void>;
}

const BatchApproveDialog: React.FC<BatchApproveDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [checked, setChecked] = useState('');
  const [textValue, setTextValue] = useState<string>('');
  const [messageApi, contextHolder] = message.useMessage();

  const { data: batchApproveSelections } = useQuery({
    queryKey: ['batchApproveSelections'],
    queryFn: async () => {
      const resp = await getBatchApproveSelections();
      if (resp.status !== 200) {
        return [];
      }
      const selections = resp.data.map((item) => ({
        label: item.dictDesc,
        value: item.dictKey,
      }));
      return selections;
    },
    refetchOnWindowFocus: false,
  });

  const handleRadioChange = (e: RadioChangeEvent) => {
    setTextValue('');
    setChecked(e.target.value as string);
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target.value);
  };

  const handleOk = async () => {
    if (!checked) {
      messageApi.warning('Please select a reason');
      return;
    }
    if (checked === 'remark' && textValue.length === 0) {
      messageApi.warning('Please provide additional comments.');
      return;
    }
    const approveKeys = checked === 'remark' ? '' : checked;
    await onConfirm(approveKeys, textValue);
    onClose();
  };

  const handleCancel = () => {
    setChecked('');
    setTextValue('');
    onClose();
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={isOpen}
        title="Please record the reason why these batch of artworks has been approved in below"
        width={800}
        onCancel={handleCancel}
        onOk={handleOk}
        destroyOnClose
      >
        <Radio.Group onChange={handleRadioChange} style={{ width: '100%' }}>
          <Row gutter={20}>
            {batchApproveSelections?.map((reason, index) => (
              <Col span={12} key={index}>
                <Radio value={reason.value}>{reason.label}</Radio>
              </Col>
            ))}
          </Row>
          <Row gutter={20}>
            <Col span={12}>
              <Radio value="remark">Remark</Radio>
            </Col>
          </Row>
        </Radio.Group>

        <Input.TextArea
          placeholder="Please provide additional comments."
          style={{ marginTop: 20 }}
          rows={4}
          maxLength={6}
          disabled={checked !== 'remark'}
          value={textValue}
          onChange={handleInputChange}
          allowClear
        />
      </Modal>
    </>
  );
};

export default BatchApproveDialog;
